import { useEffect, useMemo } from 'react'
import { fetchOrder } from 'actions/OrderActions'
import { useAppDispatch, useAppSelector, shallowEqual } from 'hooks/reduxHooks'

// exposable → hotel/other item; safe to show the amount + count it in the total
// flight    → flight item; amount withheld (a follow-up renders a "Sign up to view saving" CTA)
// pending   → order not loaded/errored; fail closed, amount withheld, not counted
export type PotentialSavingsRowStatus = 'exposable' | 'flight' | 'pending'

export interface PotentialSavingsRow {
  item: App.LuxPlusPotentialSavingsItem
  status: PotentialSavingsRowStatus
}

interface UsePotentialSavingsRowsResult {
  rows: Array<PotentialSavingsRow>
  totalAmount: number
  hasFlightSavings: boolean
  fetching: boolean
}

/**
 * Flights: LuxPlus+ flight potential savings are stored + API-exposed but the
 * amount must not be shown to customers (member-vs-public flight prices aren't
 * exposable). The potential-savings API carries no item type, so we join each
 * row to its order to classify it (mirrors useOrderItemName's flight branch,
 * over the whole list — hooks can't run per-row in a loop). Flight rows are
 * kept but tagged so a follow-up can render a masked "Sign up to view saving"
 * CTA; their amount is excluded from the total. Fail closed: a row is
 * 'exposable' only once its order has loaded AND is confirmed non-flight.
 * Remove once the API returns an item type (or excludes flights server-side).
 */
export default function usePotentialSavingsRows(
  items: Array<App.LuxPlusPotentialSavingsItem>,
): UsePotentialSavingsRowsResult {
  const dispatch = useAppDispatch()

  const orderIds = useMemo(
    () => Array.from(new Set(items.map(item => item.orderId).filter(Boolean))) as Array<string>,
    [items],
  )

  // Same fetch useOrder does, but for every order the list references (deduped, redux-cached).
  useEffect(() => {
    orderIds.forEach(orderId => dispatch(fetchOrder(orderId)))
  }, [orderIds, dispatch])

  // Scope the selectors to the orders this list references (with shallowEqual) so
  // the component doesn't re-render on unrelated order loads elsewhere in the app.
  const orders = useAppSelector(state => {
    const result: Record<string, App.Order> = {}
    for (const id of orderIds) {
      const order = state.orders.orders[id]
      if (order) result[id] = order
    }
    return result
  }, shallowEqual)
  const orderErrors = useAppSelector(state => {
    const result: Record<string, unknown> = {}
    for (const id of orderIds) {
      if (state.orders.orderErrors[id]) result[id] = state.orders.orderErrors[id]
    }
    return result
  }, shallowEqual)

  return useMemo(() => {
    const rows: Array<PotentialSavingsRow> = items.map(item => {
      const order = item.orderId ? orders[item.orderId] : undefined
      // No orderId → can't verify it isn't a flight, so withhold it (fail closed).
      // In practice order_id is always present (NOT NULL unique key); this is a guard.
      if (!order) return { item, status: 'pending' }
      const isFlight = order.flightItems?.some(flightItem => flightItem.id === item.itemId)
      return { item, status: isFlight ? 'flight' : 'exposable' }
    })

    const totalAmount = rows
      .filter(row => row.status === 'exposable')
      .reduce((sum, row) => sum + (row.item.displayAmount ?? 0), 0)

    // An order is settled once it has loaded or errored; otherwise we're still waiting on it.
    const fetching = orderIds.some(orderId => !orders[orderId] && !orderErrors[orderId])

    return {
      rows,
      totalAmount,
      hasFlightSavings: rows.some(row => row.status === 'flight'),
      fetching,
    }
  }, [items, orders, orderErrors, orderIds])
}
